import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  Stack,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Person as PersonIcon,
  Create as CreateIcon,
  MenuBook as CourseIcon,
  OndemandVideo as VideoIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { Course } from '../types';
import PdfDisplay from './PdfDisplay';
import CourseCopyDialog from './CourseCopyDialog';

interface CourseDetailsModalProps {
  course: Course | null;
  open: boolean;
  onClose: () => void;
}

const CourseDetailsModal: React.FC<CourseDetailsModalProps> = ({ course, open, onClose }) => {
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const handleOpenCopyDialog = () => {
    setCopyDialogOpen(true);
  };

  const handleCloseCopyDialog = () => {
    setCopyDialogOpen(false);
  };

  const handleCopySuccess = () => {
    setSnackbarMessage('Cours copié avec succès dans votre atelier !');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  if (!course) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div">
          {course.title}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* PDF Viewer */}
          {course.preview_pdf_url && (
            <Box sx={{ width: '100%' }}>
              <PdfDisplay url={course.preview_pdf_url} title={course.title} />
            </Box>
          )}

          {!course.preview_pdf_url && (
            <Box
              sx={{
                width: '100%',
                height: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                border: '1px dashed #ccc',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Aucun aperçu PDF disponible pour ce cours
              </Typography>
            </Box>
          )}

          <Divider />

          {/* Course Information */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Informations
            </Typography>

            <Stack spacing={2}>
              {/* Current Owner */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="primary" />
                <Typography variant="body1">
                  <strong>Propriétaire :</strong> {course.created_by_name || 'Inconnu'}
                </Typography>
              </Box>

              {/* Original Author */}
              {course.original_author_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreateIcon color="secondary" />
                  <Typography variant="body1">
                    <strong>Auteur original :</strong> {course.original_author_name}
                  </Typography>
                </Box>
              )}

              {/* Tags */}
              {course.tags && course.tags.length > 0 && (
                <Box>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Tags :</strong>
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                    {course.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Reference Elements */}
          {(course.quick_recap_course_title || course.quick_recap_video_title) && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" gutterBottom>
                  Éléments de référence (Quick Recap)
                </Typography>

                <Stack spacing={1.5}>
                  {course.quick_recap_course_title && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CourseIcon color="primary" />
                      <Typography variant="body1">
                        <strong>Cours :</strong> {course.quick_recap_course_title}
                      </Typography>
                    </Box>
                  )}

                  {course.quick_recap_video_title && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VideoIcon color="error" />
                      <Typography variant="body1">
                        <strong>Vidéo :</strong> {course.quick_recap_video_title}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button
            onClick={handleOpenCopyDialog}
            variant="contained"
            color="primary"
            startIcon={<CopyIcon />}
          >
            Copier vers mon atelier
          </Button>
          <Button onClick={onClose} variant="outlined">
            Fermer
          </Button>
        </Box>
      </DialogActions>

      {/* Copy Dialog */}
      <CourseCopyDialog
        course={course}
        open={copyDialogOpen}
        onClose={handleCloseCopyDialog}
        onSuccess={handleCopySuccess}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default CourseDetailsModal;
